import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../../components/common/Button';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import { createLanguage, getLanguageById, LANGUAGE_NAME_MAX_LENGTH, updateLanguage } from '../../services/languageMasters';
import { setFlashToast } from '../../utils/flashToast';
import {
  focusFirstInvalid,
  validateRequired,
  type RequiredErrors,
  type RequiredFieldSpec,
} from '../../utils/requiredValidation';
import { useAuth } from '../../contexts/AuthContext';
import { exceedsMax, maxCharLimitExceededError } from '../../utils/caseFieldValidation';

const READONLY_FIELD_STYLE = {
  background: 'color-mix(in srgb, var(--bg) 70%, var(--surface))',
  color: 'var(--t2)',
  cursor: 'not-allowed' as const,
};

export default function LanguageFormPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { languageId } = useParams();
  const isEdit = Boolean(languageId);
  const nav = useNavigate();

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [reqErrors, setReqErrors] = useState<RequiredErrors>({});

  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [active, setActive] = useState(true);

  const clearReqError = (key: string) => {
    setReqErrors((cur) => {
      if (!cur[key]) return cur;
      const next = { ...cur };
      delete next[key];
      return next;
    });
  };

  useEffect(() => {
    if (!isEdit || !languageId || !user) return;
    let alive = true;
    setLoading(true);
    void getLanguageById(languageId).then((res) => {
      if (!alive) return;
      if (res.success) {
        setName(res.data.name);
        setCode(res.data.code);
        setActive(res.data.active);
      } else {
        setReqErrors({ form: res.message ?? t('admin.shared.requestFailed') });
      }
      setLoading(false);
    });
    return () => {
      alive = false;
    };
  }, [isEdit, languageId, t, user]);

  const requiredSpecs = useMemo<RequiredFieldSpec[]>(() => {
    return [
      {
        key: 'name',
        label: t('metadata.languages.languageName'),
        value: name,
        elementId: 'language-name',
      },
    ];
  }, [name, t]);

  async function handleSave() {
    if (savingRef.current) return;

    const errs = validateRequired(requiredSpecs);
    if (exceedsMax(name, LANGUAGE_NAME_MAX_LENGTH)) {
      errs.name = maxCharLimitExceededError();
    }
    setReqErrors(errs);
    if (Object.keys(errs).length) {
      focusFirstInvalid(requiredSpecs, errs);
      return;
    }

    savingRef.current = true;
    setSaving(true);
    let keepLocked = false;
    try {
      const payload = { name: name.trim(), active };
      const res =
        isEdit && languageId ? await updateLanguage(languageId, payload) : await createLanguage(payload);
      if (!res.success) {
        setReqErrors({ form: res.message ?? t('admin.shared.requestFailed') });
        return;
      }
      setFlashToast({
        message: isEdit ? t('metadata.languageForm.updateSuccess') : t('metadata.languageForm.createSuccess'),
        variant: 'success',
      });
      nav('/metadata/languages');
      keepLocked = true;
    } finally {
      if (!keepLocked) {
        savingRef.current = false;
        setSaving(false);
      }
    }
  }

  return (
    <>
      <div className="bc">
        <span>{t('nav.groups.metadata')}</span>
        <span className="bc-sep">/</span>
        <Link to="/metadata/languages">{t('metadata.languages.title')}</Link>
        <span className="bc-sep">/</span>
        {isEdit ? t('metadata.languageForm.updateTitle') : t('metadata.languageForm.addTitle')}
      </div>

      <div className="pg-title">{isEdit ? t('metadata.languageForm.updateTitle') : t('metadata.languageForm.addTitle')}</div>

      <div className="cr-table-wrap" style={{ overflow: 'visible' }}>
        <div style={{ padding: 18 }}>
          {loading ? <div style={{ color: 'var(--t2)' }}>{t('common.loading')}</div> : null}
          {reqErrors.form ? <div className="si-err" style={{ marginBottom: 12 }}>{reqErrors.form}</div> : null}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div className="al-f">
              <div className="al-l">
                {t('metadata.languages.languageName')} <span className="req">*</span>
              </div>
              {isEdit ? (
                <input
                  id="language-name"
                  className="al-inp"
                  value={name}
                  maxLength={LANGUAGE_NAME_MAX_LENGTH}
                  placeholder={t('metadata.languageForm.namePlaceholder')}
                  disabled={loading}
                  style={reqErrors.name ? { borderColor: 'var(--red)' } : undefined}
                  aria-invalid={reqErrors.name ? true : undefined}
                  onChange={(e) => {
                    setName(e.target.value);
                    clearReqError('name');
                  }}
                />
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  <input
                    id="language-name"
                    className="al-inp"
                    style={{
                      flex: 1,
                      minWidth: 0,
                      ...(reqErrors.name ? { borderColor: 'var(--red)' } : {}),
                    }}
                    value={name}
                    maxLength={LANGUAGE_NAME_MAX_LENGTH}
                    placeholder={t('metadata.languageForm.namePlaceholder')}
                    disabled={loading}
                    aria-invalid={reqErrors.name ? true : undefined}
                    onChange={(e) => {
                      setName(e.target.value);
                      clearReqError('name');
                    }}
                  />
                </div>
              )}
              {reqErrors.name ? (
                <div style={{ fontSize: 11, color: 'var(--red)', marginTop: 6, fontWeight: 600 }}>{reqErrors.name}</div>
              ) : null}
            </div>

            {isEdit ? (
              <div className="al-f">
                <div className="al-l">{t('admin.shared.code')}</div>
                <input
                  className="al-inp"
                  value={code}
                  readOnly
                  disabled
                  style={READONLY_FIELD_STYLE}
                />
              </div>
            ) : (
              <div className="al-f">
                <div className="al-l" aria-hidden="true" style={{ visibility: 'hidden' }}>
                  {t('metadata.languages.languageName')} <span className="req">*</span>
                </div>
                <label
                  className="ccr-chk"
                  style={{ marginBottom: 0, minHeight: 34, display: 'inline-flex', alignItems: 'center' }}
                >
                  <input
                    type="checkbox"
                    checked={active}
                    disabled={loading}
                    onChange={(e) => setActive(e.target.checked)}
                  />
                  <span>{t('common.active')}</span>
                </label>
                {reqErrors.name ? (
                  <div className="si-err" aria-hidden="true" style={{ visibility: 'hidden' }}>
                    &nbsp;
                  </div>
                ) : null}
              </div>
            )}

            {isEdit ? (
              <div className="al-f">
                <label className="ccr-chk" style={{ marginBottom: 0 }}>
                  <input
                    type="checkbox"
                    checked={active}
                    disabled={loading}
                    onChange={(e) => setActive(e.target.checked)}
                  />
                  <span>{t('common.active')}</span>
                </label>
              </div>
            ) : null}
          </div>
        </div>

        <div className="tft" style={{ background: 'color-mix(in srgb, var(--bg) 60%, var(--surface))' }}>
          <div />
          <div className="pgr">
            <button className="mfb" type="button" disabled={saving} onClick={() => setCancelConfirmOpen(true)}>
              {t('common.cancel')}
            </button>
            <Button
              variant="primary"
              onClick={() => {
                void handleSave();
              }}
              disabled={saving || loading}
            >
              {saving ? t('common.saving') : t('common.save')}
            </Button>
          </div>
        </div>
      </div>

      <ConfirmDialog
        isOpen={cancelConfirmOpen}
        title={t('admin.shared.cancelChanges')}
        message={t('metadata.languageForm.cancelConfirm')}
        onClose={() => setCancelConfirmOpen(false)}
        onConfirm={() => {
          setCancelConfirmOpen(false);
          nav('/metadata/languages');
        }}
      />
    </>
  );
}
